import { useQueryClient, useMutation } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useState } from 'react';
import { SharedDriveModel } from '../../../../../../../types';
import { renameSharedDrive, UserKeys } from '../../../../../../api/users-api';
import ChangeLogDialog from '../../../../../../shared/change-log-dialog';
import SharedDialog from '../../../../../../shared/shared-dialog';
import { handleErrors } from '../../../../../../utils/errors';
import { useStore } from '../../../../../state/zustand-store';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../../../utils/roles/client-roles';
import { useAnalytics } from '../../../../../../shared/analytics/create-events';
import { ModuleList } from '../../../../../../shared/analytics/module-list';
import * as analytics from '../../../../../../shared/analytics/analytics.json';
import { Messages } from '../../../../../../utils/message-constants';
import { useUserSettings } from '../../../../../../utils/use-user-settings';
import { isFeatureEnabled } from '../../../../../../utils/feature-flags';
import TextField from '@mui/material/TextField';
interface SharedDriveRenameDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedDrive: SharedDriveModel;
  setSelectedDrive: Dispatch<SetStateAction<SharedDriveModel | null>>;
  changeLogRoles: string[];
}

export default function SharedDrivesRenameDialog({
  open,
  handleClose,
  changeLogRoles,
  selectedDrive,
  setSelectedDrive,
}: SharedDriveRenameDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState(selectedDrive.name);
  const { setSnackbar } = useStore();
  const { features, adminEmail, currentTier } = useUserSettings();

  const url = window.location.href.toLowerCase();
  const isSharedDrives = url.includes('shared');
  const isDriveExplorer = url.includes('explorer');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      renameSharedDrive(selectedDrive.id, newName.trim(), changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Renamed('Shared drive'),
      });

      function updateDriveData(data: SharedDriveModel[] | undefined) {
        if (!data) return;
        return data.map((drive) =>
          drive.id === selectedDrive.id ? { ...drive, name: newName } : drive
        );
      }

      if (currentTier === 'enterprise') {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.SharedDrivesIndexedPage],
        });
      } else if (!isFeatureEnabled('showPaginatedSharedDrives', features)) {
        queryClient.setQueryData(
          [UserKeys.SharedDrivesForDomain],
          updateDriveData
        );
      } else {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.SharedDrivesForDomainPage],
        });
      }

      const queries = queryClient.getQueriesData<SharedDriveModel[]>({
        queryKey: [UserKeys.SharedDrivesForUser],
      });

      queries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, updateDriveData(data));
      });

      setChangeLogMessage('');
      setSelectedDrive(null);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Renaming('the shared drive'),
      });
    },
    onSettled: () => {
      if (isSharedDrives) {
        createEvent(analytics.drive.drive_explorer.shared_drives.rename_drive, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.sharedDrives,
        });
      } else if (isDriveExplorer) {
        createEvent(analytics.drive.drive_explorer.shared_drives.rename_drive, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.driveExplorer,
        });
      } else {
        createEvent(
          analytics.user_management.user_details.drive.shared_drives
            .rename_drive,
          {
            module: ModuleList.modules.userManagement.name,
            section: ModuleList.modules.userManagement.sections.drive,
          }
        );
      }
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DriveRoles.CanRenameSharedDrives.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        setSelectedDrive(null);
        handleClose();
      }}
      title={`Rename Shared Drive`}
      actingAs={adminEmail}
      dialogContent={
        <>
          <TextField
            fullWidth
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </>
      }
      primaryButton={{
        title: 'Save Changes',
        action: handleSubmit,
        loading: isPending,
        disabled:
          selectedDrive.name === newName.trim() || newName.trim().length === 0,
      }}
    />
  );
}
